import { ChangeEventHandler } from 'react';
import { useController, UseControllerProps, FieldValues, FieldPath } from 'react-hook-form';

import { useLingui } from '@lingui/react';
import ErrorOutline from '@mildang/icons/react/error-outline';
import { css } from '@mildang/styled-system/css';
import { HStack } from '@mildang/styled-system/jsx';
import { useMergeRefs } from 'rooks';

import TextField, { CustomTextFieldProps } from './TextField';
function castToNumber(value: string | number) {
  if (value === '') return value;
  const number = Number(value);
  return isNaN(number) ? value : number;
}

export type TextFieldHookProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = CustomTextFieldProps &
  UseControllerProps<TFieldValues, TName> & {
    validateBeforeOnChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    requiredMessage?: string;
    autoSuccess?: boolean;
    hideErrorMessage?: boolean;
    valueAsNumber?: boolean;
    showErrorIcon?: boolean;
    textLengthLimit?: number;
  };

function TextFieldHook<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  rules,
  defaultValue,
  control,
  shouldUnregister,
  requiredMessage,
  autoSuccess = false,
  validateBeforeOnChange,
  hideErrorMessage = false,
  valueAsNumber = false,
  showErrorIcon = false,
  inputRef,
  textLengthLimit,
  ...props
}: TextFieldHookProps<TFieldValues, TName>) {
  const required = props.required;
  const { i18n } = useLingui();
  const {
    field: { ref, onChange, ...inputProps },
    fieldState: { isDirty, error },
    formState: { isSubmitting },
  } = useController({
    name,
    control,
    rules: {
      required: required
        ? requiredMessage ?? i18n.t({ message: '필수 정보입니다.', id: '필수 정보입니다.' })
        : undefined,
      ...rules,
    },
    defaultValue: defaultValue || ('' as any),
    shouldUnregister,
  });

  const mergedRef = useMergeRefs(ref, inputRef);

  return (
    <TextField
      onChange={(e) => {
        const isValid = validateBeforeOnChange?.(e) ?? true;
        if (isValid) {
          let value: string | number = e.target.value;
          if (textLengthLimit) {
            value = value.slice(0, textLengthLimit);
          }

          if (valueAsNumber) {
            value = castToNumber(value);
          }

          onChange({
            ...e,
            target: {
              ...e.target,
              value,
            },
          });
        }
      }}
      {...inputProps}
      disabled={isSubmitting}
      helperText={
        hideErrorMessage ? (
          ''
        ) : (
          <HStack gap={0} alignItems="center">
            {error?.message && showErrorIcon && (
              <ErrorOutline
                className={css({
                  color: 'mui.error.dark',
                  fontSize: '[16px]',
                  mr: '4px',
                })}
              />
            )}
            {error?.message}
          </HStack>
        )
      }
      error={!!error}
      success={autoSuccess && !error && isDirty}
      inputRef={mergedRef}
      {...props}
    />
  );
}

export default TextFieldHook;
